import React, { useEffect, useMemo, useRef, useState } from 'react';

import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { PanelMenu } from 'primereact/panelmenu';

import { filterMenuItemsByProductEdition } from '../config/productEdition';
import { useVisibilityClasses } from '../utils';

import '../ui/css/CustomMenuItem.css';

function filterVisibleItems(
  items: MenuItem[],
  visibilityClasses: Record<string, boolean>
): MenuItem[] {
  if (
    visibilityClasses['wholeui__roles'] ||
    localStorage.getItem('isTestMode') === 'true'
  ) {
    return items;
  }
  return items
    .filter((item) => {
      const isVisible =
        item.className === 'parent' ||
        (item.className ? visibilityClasses[item.className] ?? false : true);
      return isVisible;
    })
    .map((item) => {
      const filteredSubItems = item.items
        ? filterVisibleItems(item.items as MenuItem[], visibilityClasses)
        : undefined;
      return {
        ...item,
        items:
          filteredSubItems && filteredSubItems.length > 0
            ? filteredSubItems
            : undefined
      };
    });
}

function filterBySearch(items: MenuItem[], searchQuery: string): MenuItem[] {
  if (!searchQuery.trim()) return items;

  const searchLower = searchQuery.toLowerCase().trim();

  const result: MenuItem[] = [];

  items.forEach((item) => {
    const itemLabel = item.label?.toLowerCase() || '';
    const matchesSearch = itemLabel.includes(searchLower);
    
    let filteredSubItems: MenuItem[] = [];
    if (item.items) {
      filteredSubItems = filterBySearch(item.items as MenuItem[], searchQuery);
    }
    
    const hasMatchingSubItems = filteredSubItems.length > 0;
    
    if (matchesSearch || hasMatchingSubItems) {
      if (item.items) {
        if (matchesSearch) {
          result.push({
            ...item,
            items: item.items,
            expanded: true
          });
        } else if (hasMatchingSubItems) {
          result.push({
            ...item,
            items: filteredSubItems,
            expanded: true
          });
        }
      } else {
        result.push({
          ...item,
          expanded: true
        });
      }
    }
  });

  return result;
}

function CustomMenuItem() {
  const visibilityClasses = useVisibilityClasses();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [panelKey, setPanelKey] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleIconClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      setIsSearchFocused(true);
    }
  };

  const handleInputFocus = () => {
    setIsSearchFocused(true);
  };

  const handleInputBlur = () => {
    setIsSearchFocused(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const shouldExpand = isSearchFocused || searchQuery;

  useEffect(() => {
    setPanelKey(prev => prev + 1);
  }, [searchQuery]);

  const items: MenuItem[] = useMemo(() => [
    {
      label: 'Главная страница',
      icon: 'pi pi-fw pi-home',
      url: '/',
      className: 'tasks-nav-btn__roles'
    },
    {
      label: 'Календарь назначений',
      icon: 'pi pi-fw pi-calendar-plus',
      url: '/calendarAssignments',
      className: 'calendarAssignments-nav-btn__roles'
    },
    {
      label: 'Администрирование',
      icon: 'pi pi-fw pi-cog',
      className: 'parent',
      items: [
        {
          label: 'Функциональная структура',
          icon: 'pi pi-fw pi-sitemap',
          url: '/functionalStructure',
          className: 'functionalStructure-nav-btn__roles'
        },
        {
          label: 'Сотрудники',
          icon: 'pi pi-fw pi-users',
          url: '/employeesTable',
          className: 'employees-nav-btn__roles'
        },
        {
          label: 'Навыки и допуски',
          icon: 'pi pi-fw pi-id-card',
          url: '/skillsAndCertifications',
          className: 'attainments-nav-btn__roles'
        },
        {
          label: 'Работы',
          icon: 'pi pi-fw pi-chart-bar',
          url: '/operationTypes',
          className: 'operationTypes-nav-btn__roles'
        },
        {
          label: 'Единицы измерения',
          icon: 'pi pi-fw pi-calculator',
          url: '/measureUnit',
          className: 'measureUnits-nav-btn__roles'
        },
        {
          label: 'Географические зоны',
          icon: 'pi pi-fw pi-map-marker',
          url: '/geographicalZones',
          className: 'geographicalZones-nav-btn__roles'
        },
        {
          label: 'Реестр средних скоростей',
          icon: 'pi pi-fw pi-stopwatch',
          url: '/avgTransportSpeed',
          className: 'avgTransportSpeed-nav-btn__roles'
        },
        {
          label: 'Роли',
          className: 'parent',
          icon: 'pi pi-fw pi-user',
          items: [
            {
              label: 'Конфигуратор ролей',
              icon: 'pi pi-fw pi-id-card',
              url: 'complexRole',
              className: 'complexRoles-nav-btn__roles'
            },
            {
              label: 'Реестр микро-ролей',
              icon: 'pi pi-fw pi-key',
              url: 'roles',
              className: 'roles-nav-btn__roles'
            }
          ]
        },
        {
          label: 'Наряды',
          icon: 'pi pi-fw pi-wrench',
          className: 'parent',
          items: [
            {
              label: 'Процессы обработки нарядов',
              icon: 'pi pi-fw pi-building',
              url: '/processingWorkOrders',
              className: 'processingWorkOrders-nav-btn__roles'
            },
            {
              label: 'Статусы нарядов',
              icon: 'pi pi-fw pi-sliders-h',
              url: '/taskStatuses',
              className: 'taskStatuses-nav-btn__roles'
            },
            {
              label: 'Типы нарядов',
              icon: 'pi pi-fw pi-wrench',
              url: '/tasksType',
              className: 'tasksType-nav-btn__roles'
            },
            {
              label: 'Шифры закрытия нарядов',
              icon: 'pi pi-fw pi-key',
              url: '/taskClosingCodes',
              className: 'taskClosingCodes-nav-btn__roles'
            },
            {
              label: 'Алгоритмы распределения нарядов по сотрудникам',
              icon: 'pi pi-fw pi-sort-amount-up',
              url: '/planningAlgorithms',
              className: 'planningAlgorithms-nav-btn__roles'
            },
          ]
        },
        {
          label: 'Объекты',
          icon: 'pi pi-fw pi-building',
          className: 'parent',
          items: [
            {
              label: 'Типы объектов',
              icon: 'pi pi-fw pi-database',
              url: '/assetTypes',
              className: 'assetTypes-nav-btn__roles'
            },
            {
              label: 'Характеристики объектов',
              icon: 'pi pi-fw pi-align-left',
              url: '/assetParameters',
              className: 'assetParameters-nav-btn__roles'
            },
            {
              label: 'Реестр объектов',
              icon: 'pi pi-fw pi-sitemap',
              url: '/assets',
              className: 'assets-nav-btn__roles'
            },
          ]
        },
        {
          label: 'Графики',
          icon: 'pi pi-fw pi-calendar',
          className: 'parent',
          items: [
            {
              label: 'Виды отсутствий',
              icon: 'pi pi-fw pi-calendar-times',
              url: '/absences',
              className: 'absences-nav-btn__roles'
            },
            {
              label: 'Расписания',
              icon: 'pi pi-fw pi-table',
              url: '/schedules',
              className: 'schedules-nav-btn__roles'
            },
            {
              label: 'Календари',
              icon: 'pi pi-fw pi-calendar',
              url: '/calendars',
              className: 'calendars-nav-btn__roles'
            },
          ]
        },
        
      ]
    }
  ], []);

  const filteredItems = useMemo(() => {
    let result = filterMenuItemsByProductEdition(items);
    result = filterVisibleItems(result, visibilityClasses);
    result = filterBySearch(result, searchQuery);
    return result;
  }, [items, visibilityClasses, searchQuery]);

  const hasVisibleItems = filteredItems.length > 0;

  return (
    <div className="сustomMenuItem card flex flex-column justify-content-center pl-0 pr-0">
      <div className="mb-2">
        <div className="search-container">
          <i className="pi pi-search search-icon" onClick={handleIconClick} />
          <InputText
            ref={inputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={shouldExpand ? 'Поиск по меню' : ''}
          />
          {searchQuery && (
            <i
              className="pi pi-times clear-icon"
              onClick={handleClearSearch}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>
      </div>
      {hasVisibleItems && (
        <PanelMenu
          key={panelKey}
          model={filteredItems}
          className="w-full"
        />
      )}
    </div>
  );
}

export default React.memo(CustomMenuItem);